import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Plus, Key } from "lucide-react";
import type { TFunction } from "i18next";

interface Agent {
  id: string;
  name: string;
  email: string;
  role_id: number;
}

interface AgentManagementCardProps {
  agents: Agent[];
  currentUserId: string;
  onCreateAgent: () => void;
  onResetPassword: (email: string, name: string) => void;
  getRoleName: (roleId: number) => string;
  t: TFunction<"translation", undefined>;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const AgentManagementCard: React.FC<AgentManagementCardProps> = ({
  agents,
  currentUserId,
  onCreateAgent,
  onResetPassword,
  getRoleName,
  t,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calculateOptimalPageSize = () => {
      const windowHeight = window.innerHeight;
      
      const headerHeight = 180; 
      const cardHeaderHeight = 80; 
      const paginationHeight = 100;
      const rowHeight = 65; 
      const padding = 100; 
      const personalInfoHeight = 300; 
      
      const availableHeight = windowHeight - headerHeight - cardHeaderHeight - paginationHeight - padding - personalInfoHeight;
      
      const possibleRows = Math.floor(availableHeight / rowHeight);
      
      let optimalSize = 10;
      
      if (possibleRows <= 3) {
        optimalSize = 5;
      } else if (possibleRows <= 8) {
        optimalSize = 10;
      } else {
        optimalSize = 20;
      }

      setPageSize(optimalSize);
      setPage(1); 
    };

    const timer = setTimeout(calculateOptimalPageSize, 100);

    window.addEventListener('resize', calculateOptimalPageSize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateOptimalPageSize);
    };
  }, []);

  const pageCount = Math.ceil(agents.length / pageSize);
  const paginatedAgents = useMemo(() => {
    return agents.slice((page - 1) * pageSize, page * pageSize);
  }, [agents, page, pageSize]);

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setPage(1);
  };

  return (
    <Card className="border-[#E5E7EC]" ref={cardRef}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#252A32]">
          {t("agent_management")}
        </CardTitle>
        <Button
          onClick={onCreateAgent}
          className="bg-[#105BDB] hover:bg-[#0055C4] text-white"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("create_agent")}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-[#E5E7EC] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[150px] sm:min-w-0">{t("name")}</TableHead>
                  <TableHead className="hidden sm:table-cell min-w-[200px]">{t("email")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("role")}</TableHead>
                  <TableHead className="text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedAgents.map((agent) => {
                  const isCurrentUser = agent.id === currentUserId;
                  return (
                    <TableRow key={agent.id}>
                      <TableCell className="font-medium text-[#252A32]">
                        <div className="flex flex-col">
                          <span>
                            {agent.name}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-[#6F7D95]">
                                ({t("you")})
                              </span>
                            )}
                          </span>
                          <span className="sm:hidden text-xs text-[#6F7D95] mt-1">
                            {agent.email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-[#6F7D95]">
                        {agent.email}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-[#6F7D95]">
                        {getRoleName(agent.role_id)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onResetPassword(agent.email, agent.name)}
                          className="hover:bg-[#F6F7F9] text-[#105BDB]"
                        >
                          <Key className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">{t("reset_password")}</span>
                          <span className="sm:hidden">{t("reset")}</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {agents.length > PAGE_SIZE_OPTIONS[0] && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-4 pb-16 sm:pb-4">
            <p className="text-sm text-[#6F7D95] order-2 sm:order-1">
              {t("showing")} {Math.min((page - 1) * pageSize + 1, agents.length)} - {Math.min(page * pageSize, agents.length)} {t("of")} {agents.length}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 text-[#4A5364] order-1 sm:order-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#6F7D95] whitespace-nowrap">
                  {t("rows_per_page")}
                </span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[72px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-sm text-[#6F7D95] whitespace-nowrap">
                {t("page")} {page} {t("of")} {pageCount}
              </span>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationFirst
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1) setPage(1);
                      }}
                      aria-disabled={page <= 1}
                      className={page <= 1 ? "opacity-50 pointer-events-none" : ""}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1) setPage((prev) => Math.max(prev - 1, 1));
                      }}
                      aria-disabled={page <= 1}
                      className={page <= 1 ? "opacity-50 pointer-events-none" : ""}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < pageCount) setPage((prev) => Math.min(prev + 1, pageCount));
                      }}
                      aria-disabled={page >= pageCount}
                      className={page >= pageCount ? "opacity-50 pointer-events-none" : ""}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLast
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < pageCount) setPage(pageCount);
                      }}
                      aria-disabled={page >= pageCount}
                      className={page >= pageCount ? "opacity-50 pointer-events-none" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentManagementCard;
